<template>
  <UIDialog :is-open :title :z-index size="md" @close="handleClose">
    <div v-circular-tab class="node-form">
      <UIInputAutocomplete
        v-model="assetId"
        :data="assetsList"
        placeholder="Find or create new"
        dynamic
        @click:new="handleCreate"
        @ready="handleReady"
      />
      <UIButtonsGroup>
        <template #default="{ short }">
          <UIButtonClose :short @click="handleClose" />
          <UIButtonSave
            :short
            :disabled="!isValid"
            label="Add"
            type="submit"
            @click="handleComplete"
          />
        </template>
      </UIButtonsGroup>
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue'
import { orderBy } from 'lodash'

import { ModalClass } from '@types'

import { ASSET_FIELD } from '@/const'

import { onCreateAsset } from '@/entities/transactions/components/utils/helpers'

import { useModalsStore } from '@/store/modals'
import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIDialog } from '@ui/modals'
import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { UIInputAutocomplete } from '@ui/fields'

type Props = {
  modal: ModalClass<any>
  title: string
  restrictions?: string[]
}

type Emits = {
  'complete': [assetId: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()

const assetId = ref<string>()

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const isValid = computed(() => !!assetId.value)

const assetsList = computed(() => orderBy(Array.from(assetsBunchStore.getList.values())
  .filter(instance => props.restrictions?.includes(instance.field<string>(ASSET_FIELD.TYPE).value))
  .map(instance => ({
    value: instance.id,
    label: instance.field<string>(ASSET_FIELD.NAME).value,
  })), ['label']))

provide('restrictions', props.restrictions)

const handleClose = () => {
  props.modal.close()
}

const handleComplete = () => {
  if (!assetId.value) return
  emit('complete', assetId.value)
  handleClose()
}

const handleCreate = (value: string, callback: () => void) => {
  callback()
  const instance = assetsBunchStore.createElement()
  onCreateAsset(value, props.restrictions, instance, modalsStore, (data: string) => {
    assetId.value = data
    handleComplete()
  })
}

const handleReady = (el: HTMLInputElement) => {
  el.focus()
}
</script>

<style scoped>
.node-form {
  @apply flex flex-col gap-4;
}
</style>
